import { useState } from 'react';
import { Search } from 'lucide-react';
import { dayChoices, statusChoices } from './export-history-words';
import type { Filters } from './use-export-history';

interface FiltersProps {
  applied: Filters;
  types: string[];
  busy: boolean;
  onFind: (filters: Filters) => void;
}

/**
 * The history's search: words of the filed name (or a document id), a document type, a status and a day, applied by
 * Find or Enter, as CapturePoint's Activity filters. The types are the job's (its file naming's catalog).
 */
export function ExportHistoryFilters({ applied, types, busy, onFind }: FiltersProps) {
  const [draft, setDraft] = useState<Filters>(applied);
  const set = (key: keyof Filters) => (value: string) => setDraft((d) => ({ ...d, [key]: value }));
  return (
    <form
      className="paige-export-filters"
      role="search"
      aria-label="Export history filters"
      onSubmit={(event) => (event.preventDefault(), onFind(draft))}
    >
      <input
        aria-label="Search exported documents"
        placeholder="Name or document id"
        value={draft.q}
        onChange={(event) => set('q')(event.target.value)}
      />
      <Selects draft={draft} types={types} set={set} />
      <button type="submit" className="quiet-button small" disabled={busy}>
        <Search size={12} aria-hidden="true" /> Find
      </button>
    </form>
  );
}

/** The type, status and day choices. */
function Selects(props: {
  draft: Filters;
  types: string[];
  set: (key: keyof Filters) => (value: string) => void;
}) {
  const { draft, types, set } = props;
  const typeChoices = [
    { value: '', label: 'All types' },
    ...types.map((type) => ({ value: type, label: type })),
  ];
  return (
    <>
      <Choice
        label="Filter by document type"
        value={draft.type}
        choices={typeChoices}
        onChange={set('type')}
      />
      <Choice
        label="Filter by export status"
        value={draft.state ?? ''}
        choices={statusChoices}
        onChange={set('state')}
      />
      <Choice
        label="Filter by export date"
        value={draft.day}
        choices={dayChoices()}
        onChange={set('day')}
      />
    </>
  );
}

function Choice(props: {
  label: string;
  value: string;
  choices: readonly { value?: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <select
      aria-label={props.label}
      value={props.value}
      onChange={(event) => props.onChange(event.target.value)}
    >
      {props.choices.map((choice) => (
        <option key={choice.label} value={choice.value ?? ''}>
          {choice.label}
        </option>
      ))}
    </select>
  );
}
